/**
 * MuhurtaVisibilityService - Event-driven muhurta chakra visibility management
 *
 * NEW ARCHITECTURE:
 * - Subscribes to 'chart.activated' events
 * - Checks muhurta chakra visibility settings
 * - Makes API calls for muhurta data only when needed
 * - Emits 'muhurta.visibility.changed' events
 */

import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { chakraDataService } from './ChakraDataService';
import type { ChakraType } from './ChakraDataService';
import { devLog, devWarn, devError } from '../utils/devLog';

interface MuhurtaVisibilityState {
  chartId: string;
  isVisible: boolean;
  isLoading: boolean;
  hasData: boolean;
  lastChecked: Date | null;
  error: string | null;
}

/**
 * Muhurta Visibility Service
 * Event-driven service for managing muhurta chakra visibility across multiple charts
 */
class MuhurtaVisibilityService {
  private visibilityStates = new Map<string, MuhurtaVisibilityState>();
  private subscriptions: Array<{ unsubscribe: () => void }> = [];

  constructor() {
    this.setupEventSubscriptions();
    // Debug: Initialized (production: logs disabled)
  }

  /**
   * Setup event subscriptions for chart events
   */
  private setupEventSubscriptions(): void {
    // Subscribe to chart.activated events
    const chartActivatedSub = globalEventStream.subscribe('chart.activated', async (event: any) => {
      const chartId = event.chartId;
      if (!chartId) {
        devWarn('⚠️ MuhurtaVisibilityService: Received chart.activated without chartId');
        return;
      }

      // Debug: Chart activated (production: logs disabled)
      await this.checkAndHandleMuhurtaVisibility(chartId);
    });

    // Subscribe to chart.loaded events for initial setup
    const chartLoadedSub = globalEventStream.subscribe('chart.loaded', (event: any) => {
      const chartId = event.chartId;
      if (!chartId) return;

      // Debug: Chart loaded (production: logs disabled)
      this.initializeVisibilityState(chartId);
    });

    // Subscribe to muhurta settings changes
    const muhurtaSettingsChangedSub = globalEventStream.subscribe('muhurta.settings.changed', async (event: any) => {
      const chartId = event.chartId;
      if (!chartId) return;

      // Debug: Muhurta settings changed (production: logs disabled)
      await this.checkAndHandleMuhurtaVisibility(chartId);
    });

    this.subscriptions.push(chartActivatedSub, chartLoadedSub, muhurtaSettingsChangedSub);
  }

  /**
   * Initialize visibility state for a chart
   */
  private initializeVisibilityState(chartId: string): void {
    if (!this.visibilityStates.has(chartId)) {
      this.visibilityStates.set(chartId, {
        chartId,
        isVisible: false,
        isLoading: false,
        hasData: false,
        lastChecked: null,
        error: null
      });
      // Debug: Initialized visibility state (production: logs disabled)
    }
  }

  /**
   * Check muhurta visibility settings and handle accordingly
   */
  async checkAndHandleMuhurtaVisibility(chartId: string): Promise<void> {
    try {
      // Initialize state if needed
      this.initializeVisibilityState(chartId);

      const state = this.visibilityStates.get(chartId)!;

      // Prevent concurrent checks
      if (state.isLoading) {
        devLog(`⏳ MuhurtaVisibilityService: Already checking visibility for ${chartId}`);
        return;
      }

      state.isLoading = true;
      state.error = null;

      // Get chart data from MultipleChartsContext
      const multipleChartsContext = window.multipleChartsContext;
      if (!multipleChartsContext) {
        throw new Error('MultipleChartsContext not available');
      }

      const chart = multipleChartsContext.getChart(chartId);
      if (!chart) {
        throw new Error(`Chart ${chartId} not found in context`);
      }

      // Check visibility setting from chart settings
      const shouldShowMuhurta = chart.settings?.showMuhurtaChakra || false;
      const wasVisible = state.isVisible;

      devLog(`🔍 MuhurtaVisibilityService: Visibility check for ${chartId} - shouldShow: ${shouldShowMuhurta}, wasVisible: ${wasVisible}`);

      // Update visibility state
      state.isVisible = shouldShowMuhurta;
      state.lastChecked = new Date();

      if (shouldShowMuhurta) {
        // Muhurta should be visible - check if we need to load data
        if (!state.hasData) {
          devLog(`📊 MuhurtaVisibilityService: Loading muhurta data for ${chartId}`);
          await this.loadMuhurtaData(chartId);
        }

        // Emit visibility event if state changed
        if (!wasVisible) {
          this.emitVisibilityEvent(chartId, true);
        }

        // Trigger rendering
        await this.triggerMuhurtaRendering(chartId);

      } else {
        // Muhurta should be hidden
        if (wasVisible) {
          this.emitVisibilityEvent(chartId, false);
        }

        // Hide muhurta divisions
        await this.hideMuhurtaDivisions(chartId);
      }

    } catch (error) {
      const state = this.visibilityStates.get(chartId);
      if (state) {
        state.error = error instanceof Error ? error.message : String(error);
      }

      devError(`❌ MuhurtaVisibilityService: Failed to check visibility for ${chartId}:`, error);

    } finally {
      // Clear loading flag
      const state = this.visibilityStates.get(chartId);
      if (state) {
        state.isLoading = false;
      }
    }
  }

  /**
   * Load muhurta data from API/service
   */
  private async loadMuhurtaData(chartId: string): Promise<void> {
    try {
      // Get chart from context
      const multipleChartsContext = window.multipleChartsContext;
      if (!multipleChartsContext) return;
      const chart = multipleChartsContext.getChart(chartId);

      if (!chart || !chart.apiResponse) {
        devWarn(`⚠️ MuhurtaVisibilityService: No API response available for muhurta data load - ${chartId}`);
        return;
      }

      // Extract sunrise longitude from API response
      const sunriseLongitude = chart.apiResponse?.astronomical?.sunrise_surya_longitude;
      if (!sunriseLongitude) {
        devWarn(`⚠️ MuhurtaVisibilityService: No sunrise longitude in API response for ${chartId}`);
        return;
      }

      // Use ChakraDataService to get muhurta data
      const muhurtaRequest = {
        chartId,
        chakraType: 'muhurta' as ChakraType,
        options: { sunriseLongitude }
      };
      const muhurtaResponse = await chakraDataService.getChakraData(muhurtaRequest);
      const muhurtaData = muhurtaResponse.success ? muhurtaResponse.data : null;

      if (muhurtaData) {
        const state = this.visibilityStates.get(chartId);
        if (state) {
          state.hasData = true;
        }
        devLog(`✅ MuhurtaVisibilityService: Loaded muhurta data for ${chartId}`);
      }

    } catch (error) {
      devError(`❌ MuhurtaVisibilityService: Failed to load muhurta data for ${chartId}:`, error);
    }
  }

  /**
   * Trigger muhurta rendering via event system (Universal Chakra Architecture)
   */
  private async triggerMuhurtaRendering(chartId: string): Promise<void> {
    try {
      // Emit muhurta toggle event - TabChartRenderer will handle rendering via UniversalChakraRenderer
      globalEventStream.emit({
        id: `muhurta-show-${chartId}-${Date.now()}`,
        type: 'muhurta.chakra.toggle',
        source: 'MuhurtaVisibilityService',
        chartId,
        timestamp: Date.now(),
        data: {
          showMUHURTAChakra: true
        }
      });

      devLog(`🎨 MuhurtaVisibilityService: Emitted muhurta.chakra.toggle(true) for ${chartId}`);

    } catch (error) {
      devError(`❌ MuhurtaVisibilityService: Failed to trigger muhurta rendering for ${chartId}:`, error);
    }
  }

  /**
   * Hide muhurta divisions via event system (Universal Chakra Architecture)
   */
  private async hideMuhurtaDivisions(chartId: string): Promise<void> {
    try {
      // Emit muhurta toggle event to hide - UniversalChakraRendererManager will handle
      globalEventStream.emit({
        id: `muhurta-hide-${chartId}-${Date.now()}`,
        type: 'muhurta.chakra.toggle',
        source: 'MuhurtaVisibilityService',
        chartId,
        timestamp: Date.now(),
        data: {
          showMUHURTAChakra: false
        }
      });

      devLog(`🙈 MuhurtaVisibilityService: Emitted muhurta.chakra.toggle(false) for ${chartId}`);

    } catch (error) {
      devError(`❌ MuhurtaVisibilityService: Failed to hide muhurta divisions for ${chartId}:`, error);
    }
  }

  /**
   * Emit visibility change event
   */
  private emitVisibilityEvent(chartId: string, isVisible: boolean): void {
    try {
      globalEventStream.emit({
        id: `muhurta-visibility-${chartId}-${Date.now()}`,
        type: 'muhurta.visibility.changed',
        source: 'MuhurtaVisibilityService',
        chartId,
        timestamp: Date.now(),
        metadata: {
          isVisible,
          hasData: this.visibilityStates.get(chartId)?.hasData || false
        }
      });

      // Debug: Emitted visibility event (production: logs disabled)
    } catch (error) {
      devError(`❌ MuhurtaVisibilityService: Failed to emit visibility event:`, error);
    }
  }

  /**
   * Manually set muhurta visibility for a chart
   */
  async setMuhurtaVisibility(chartId: string, isVisible: boolean): Promise<void> {
    try {
      // Update chart settings
      const multipleChartsContext = window.multipleChartsContext;
      if (multipleChartsContext) {
        multipleChartsContext.updateChart(chartId, {
          settings: {
            showMuhurtaChakra: isVisible
          }
        });
      }

      // Trigger visibility check
      await this.checkAndHandleMuhurtaVisibility(chartId);

    } catch (error) {
      devError(`❌ MuhurtaVisibilityService: Failed to set visibility for ${chartId}:`, error);
    }
  }

  /**
   * Get visibility statistics for all charts
   */
  getStats() {
    const stats = {
      totalCharts: this.visibilityStates.size,
      visibleCharts: 0,
      chartsWithData: 0,
      loadingCharts: 0,
      chartsWithErrors: 0,
      charts: {} as any
    };

    for (const [chartId, state] of this.visibilityStates.entries()) {
      if (state.isVisible) stats.visibleCharts++;
      if (state.hasData) stats.chartsWithData++;
      if (state.isLoading) stats.loadingCharts++;
      if (state.error) stats.chartsWithErrors++;

      stats.charts[chartId] = {
        isVisible: state.isVisible,
        isLoading: state.isLoading,
        hasData: state.hasData,
        lastChecked: state.lastChecked,
        error: state.error
      };
    }

    return stats;
  }

  /**
   * Cleanup service and unsubscribe from events
   */
  cleanup(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
    this.visibilityStates.clear();
    devLog('🧹 MuhurtaVisibilityService: Cleaned up');
  }
}

// Create singleton instance
export const muhurtaVisibilityService = new MuhurtaVisibilityService();

// Make it globally available for debugging
window.muhurtaVisibilityService = muhurtaVisibilityService;